import { useState, useEffect } from 'react';
import { ACTIVE_SURVEY } from '../config/surveyConfig';
import { usePremium } from './usePremium';
import { useAuth } from './useAuth';
import { get } from '../utils/api';

/**
 * Returns true when there's an active survey the current premium user hasn't completed yet.
 * Fires one API call per login; listens for 'member-survey-completed' to clear instantly.
 */
export function useHasPendingSurvey(): boolean {
  const { user } = useAuth();
  const { isPremium } = usePremium();
  const [hasPending, setHasPending] = useState(false);

  useEffect(() => {
    if (!ACTIVE_SURVEY || !isPremium || !user?.id) {
      setHasPending(false);
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const res = await get<{ completed: boolean }>(
          `/api/survey/member?surveyId=${ACTIVE_SURVEY!.surveyId}`
        );
        if (!cancelled) {
          setHasPending(res.data.completed === false);
        }
      } catch {
        if (!cancelled) setHasPending(false);
      }
    };

    check();

    return () => { cancelled = true; };
  }, [user?.id, isPremium]);

  // Listen for instant dismissal after survey submission
  useEffect(() => {
    const handler = () => setHasPending(false);
    window.addEventListener('member-survey-completed', handler);
    return () => window.removeEventListener('member-survey-completed', handler);
  }, []);

  return hasPending;
}
